// @flow

import styled from 'styled-components';
import * as React from 'react';
import { t } from '../../../lib/i18n';
import { track } from '../../../lib/track.jsx';
import type { InternalChallenge } from '../../../lib/Challenge';
import { trackImpressionForChallenge } from '../../../lib/impressionTracking';
import sendJIEvent from '../../../lib/kwizzad-ji.jsx';

// After starting a video, the skip button displays a countdown this long.
const SkipButtonTimeout = 5000;

// If no video starts after this time (in milliseconds), the skip button is shown.
const SkipButtonErrorTimeout = 10000;


export type State = {
    showSkipButton: boolean,
    skipButtonTimeLeft: number,
    loading: boolean,
};


export type Props = {
    className?: string,
    next: (() => void),
    challengeCount: number,
    skipToLastChallengeIfThisIsTheLastWaterfallChallenge: (() => void),
    skipToNextNonWaterfallChallenge: (() => void),
    challenge: InternalChallenge,
    publisherAppDomainName: ?string,
    publisherAppName: ?string,
};

const SkipButton = styled.button`
    display: inline-block;
    position: absolute;
    right: -2px;
    bottom: 20%;
    font-size: 1.25rem;
    color: #fff;
    padding: 0.5em 0.75em;
    cursor: pointer;
    background-color: rgba(0,0,0,0.65);
    border: solid 2px rgba(255,255,255,0.2);
    font-weight: 400;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.5s ease-out;

    &:focus {
        outline: none;
    }
    &:hover,
    &:active {
        background-color: rgba(32,32,32,0.65);
    }
    &.visible {
        pointer-events: inherit;
        opacity: 1;
    }
    &.counting {
        opacity: 0.5;
    }
`;


class SkippableChallenge extends React.Component<Props, State> {
    loadingTimeout: number;
    skipButtonTimerInterval: number;
    skipButtonStartTime: Date;

    state = {
        loading: true,
        showSkipButton: false,
        skipButtonTimeLeft: 0,
    };


    componentDidMount() {
        this.registerWindowEvents();
        // If the player does not start within some seconds, show a skip button so the
        // user is not stuck
        this.startLoadingTimeout();
    }


    componentWillUnmount() {
        clearInterval(this.skipButtonTimerInterval);
        if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
        }
        try {
            window.removeEventListener('message', this.handleMessageEvent);

            // This is verbose, but makes the code analyzable by FlowType
            window.top.eventPassback = undefined;
            window.top.eventComplete = undefined;
            window.top.eventQuartile = undefined;
            window.top.eventError = undefined;
            window.top.eventReady = undefined;
            window.top.eventStarted = undefined;
            window.top.eventImpression = undefined;
        } catch (e) {
            console.log('Could not unregister window callbacks:', e);
        }
    }


    registerWindowEvents() {
        try {
            // This is verbose, but makes the code analyzable by FlowType
            Object.assign(window, {
                eventComplete: this.eventComplete.bind(this),
                eventError: this.eventError.bind(this),
                eventReady: this.eventReady.bind(this),
                eventStarted: this.eventStarted.bind(this),
                eventPassback: this.eventPassback.bind(this),
                eventImpression: this.eventImpression.bind(this),
                eventQuartile: this.eventQuartile.bind(this),
            });
            window.addEventListener('message', this.handleMessageEvent);
        } catch (e) {
            console.log('Could not register window callbacks:', e);
        }
    }

    handleMessageEvent = (e: MessageEvent) => {
        const prefix = /^kwizzad\.event/;

        // Necessary to prevent cross origin attacks
        if (window.location.origin !== e.origin) return;

        // Only handle known event types
        const data = e.data;
        if (typeof data !== 'string') return;
        if (!data.match(prefix)) return;

        // This is verbose, but makes the code analyzable by FlowType
        switch (data.replace(prefix, '')) {
            case 'Complete': this.eventComplete.call(this); break;
            case 'Error': this.eventError.call(this); break;
            case 'Ready': this.eventReady.call(this); break;
            case 'Started': this.eventStarted.call(this); break;
            case 'Passback': this.eventPassback.call(this); break;
            case 'Impression': this.eventImpression.call(this); break;
            case 'Quartile2': this.eventQuartile.call(this, 2); break;
            case 'Quartile3': this.eventQuartile.call(this, 3); break;
            case 'Quartile4': this.eventQuartile.call(this, 4); break;
            default: break;
        }
    };

    eventComplete() {
        // Don't track this, completed challenges are already tracked on the outside
        this.props.skipToNextNonWaterfallChallenge();
    }

    eventError() {
        const challenge = this.props.challenge;
        this.track('Error', {
            challengeId: this.props.challenge.id,
            'Ad Placement': challenge.placement,
            'Ad Provider': challenge.provider,
            Source: 'Komet',
        });
        // this.showSkipButtonWithTimeout(0);
        this.trackPassbackAndContinue();
    }

    eventReady() {
        this.setState({ loading: false });
        // this.track('Ready');
        clearTimeout(this.skipButtonLoadingTimeout);
        sendJIEvent('challengeReady', this.props.challenge.num);
    }

    eventStarted() {
        this.setState({ loading: false });
        // this.track('Started');
        if (this.constructor.shouldShowSkipButtonOnStart()) {
            this.showSkipButtonWithTimeout(SkipButtonTimeout);
        }
    }

    eventPassback() {
        this.trackPassbackAndContinue();
    }

    eventImpression() {
        this.setState({ loading: false });
    }

    eventQuartile(quartile: number) {
        // this.track('QuartileStarted', { quartile });
    }

    startLoadingTimeout() {
        if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
        }
        this.loadingTimeout = setTimeout(() => {
            if (this.state.loading === true) {
                console.log('Loading timeout.');
                this.eventPassback();
            }
        }, SkipButtonErrorTimeout);
    }

    trackImpression(callback: ?(() => void)) {
        const challenge = this.props.challenge;
        trackImpressionForChallenge(challenge);
        if (callback) callback();
    }

    trackAdOpportunity(adFound: boolean) {
        const challenge = this.props.challenge;
        if (!challenge) return;
        if (!adFound && !challenge.isLastOfWaterfall) return;
        track('AdOpportunity', {
            'Ad Action': adFound ? 'Ad shown successfully' : 'Ad passback',
            'Ad Placement': challenge.placement,
            'Ad Provider': adFound ? challenge.provider : 'unknown',
            'impression quantity': (adFound ? 1 : 0),
            Source: 'Komet',
            eCPM: adFound ? challenge.eCPM : 0,
            rCPM: adFound ? challenge.rCPM : 0,
        });
    }

    track(eventName: string, props: {} = {}) {
        const challenge = this.props.challenge;
        const trackedProps = {
            challengeIndex: challenge.num,
            challengeCount: this.props.challengeCount,
            type: challenge.type,
            sdkType: challenge.sdkType,
        };
        Object.assign(trackedProps, props);
        track(eventName, trackedProps);
    }

    trackPassbackAndContinue() {
        // this.track('Passback');
        if (
            this.props.challenge.X_COMPLETION_TYPE === 'onshow' || // legacy, might be found in some campaigns
            this.props.challenge.X_SKIP_TO_END_ON_PASSBACK
        ) {
            this.props.skipToLastChallengeIfThisIsTheLastWaterfallChallenge();
        } else {
            this.props.next();
        }
    }

    skip() {
        // this.track('Skip');
        this.props.skipToNextNonWaterfallChallenge();
    }


    showSkipButtonWithTimeout(milliseconds: number): void {
        console.log('Show skip button with timeout', milliseconds);
        this.setState({ showSkipButton: true });
        clearInterval(this.skipButtonTimerInterval);

        this.setState({ skipButtonTimeLeft: milliseconds });

        if (!milliseconds) return;

        this.skipButtonStartTime = new Date();
        this.skipButtonTimerInterval = setInterval(() => {
            let skipButtonTimeLeft =
                SkipButtonTimeout - (new Date() - this.skipButtonStartTime);
            if (skipButtonTimeLeft <= 0) {
                skipButtonTimeLeft = 0;
                clearInterval(this.skipButtonTimerInterval);
            }
            this.setState({ skipButtonTimeLeft });
        }, 100);
    }

    skipIfAllowed() {
        if (this.state.skipButtonTimeLeft <= 0) {
            this.skip();
        }
    }

    skipButtonClassNames(): string {
        const classNames = ['skipVideo'];
        if (this.state.skipButtonTimeLeft) {
            classNames.push('counting');
        }
        if (this.state.showSkipButton) {
            classNames.push('visible');
        }
        return classNames.join(' ');
    }

    skipButtonText(): string {
        if (this.state.skipButtonTimeLeft) {
            const seconds = Math.floor(this.state.skipButtonTimeLeft / 1000) + 1;
            return t('skipVideoTimer').replace(/{seconds}/, seconds);
        }
        return t('skipVideo');
    }

    skipButton(): React$Element<*> {
        return (<SkipButton
            className={(() => this.skipButtonClassNames())()}
            onClick={() => this.skipIfAllowed()}
        >
            {this.skipButtonText()}
        </SkipButton>)
    }

    static shouldShowSkipButtonOnStart() {
        return true;
    }
}


export default SkippableChallenge;




// WEBPACK FOOTER //
// ./src/campaign/challenges/external-sdks/SkippableChallenge.jsx